import { MicroSentryClient } from '@micro-sentry/core';
import { __assign } from 'tslib';

function isMatchingPattern(value, pattern) {
    if (Object.prototype.toString.call(pattern) === '[object RegExp]') {
        return pattern.test(value);
    }
    if (typeof pattern === 'string') {
        return value.indexOf(pattern) !== -1;
    }
    return false;
}

class BrowserMicroSentryClient extends MicroSentryClient {
    constructor(options) {
        super(options);
        this.options = options;
        this.destroyed = false;
        this._state = {};
        const { plugins = [], beforeSend = (req) => req, beforeBreadcrumb = (breadcrumb) => breadcrumb, blacklistUrls = [], ignoreErrors = [], release = undefined, } = this.options || {};
        this.plugins = plugins.map((Plugin) => new Plugin(this));
        this.beforeSend = beforeSend;
        this.beforeBreadcrumb = beforeBreadcrumb;
        this.blacklistUrls = blacklistUrls;
        this.ignoreErrors = ignoreErrors;
        this.release = release;
    }
    get state() {
        return this._state;
    }
    clearState() {
        this._state = {};
    }
    setTags(tags) {
        this.setKeyState('tags', Object.assign({}, tags));
        return this;
    }
    setTag(key, value) {
        this.extendState({ tags: { [key]: value } });
        return this;
    }
    setExtra(key, value) {
        this.extendState({ extra: { [key]: value } });
        return this;
    }
    setExtras(extras) {
        this.setKeyState('extra', Object.assign({}, extras));
        return this;
    }
    setUser(user) {
        this.setKeyState('user', Object.assign({}, user));
        return this;
    }
    clone() {
        const client = new BrowserMicroSentryClient(Object.assign(Object.assign({}, this.options), { plugins: [] }));
        client.extendState(this.state);
        return client;
    }
    withScope(fn) {
        const clone = this.clone();
        fn(clone);
        clone.destroy();
        this.setBreadcrumbs(undefined);
    }
    addBreadcrumb(breadcrumb) {
        this.extendState({
            breadcrumbs: [
                Object.assign({ timestamp: Date.now() / 1000 }, this.beforeBreadcrumb(breadcrumb)),
            ],
        });
    }
    setBreadcrumbs(breadcrumbs) {
        this.setKeyState('breadcrumbs', breadcrumbs);
    }
    captureMessage(message, level) {
        this.send(Object.assign(Object.assign({}, this.getRequestBlank()), { message,
            level }));
    }
    destroy() {
        this.destroyed = true;
        this.plugins.forEach((plugin) => {
            if (plugin.destroy) {
                plugin.destroy();
            }
        });
    }
    isIgnoredError(event) {
        if (!this.ignoreErrors.length) {
            return false;
        }
        return this.getPossibleEventMessages(event).some((message) => this.ignoreErrors.some((pattern) => isMatchingPattern(message, pattern)));
    }
    getRequestBlank() {
        return Object.assign(Object.assign({}, super.getRequestBlank()), this.state);
    }
    send(request) {
        if (this.destroyed ||
            this.isDeniedUrl(request) ||
            this.isIgnoredError(request)) {
            return;
        }
        super.send(this.beforeSend(Object.assign({ release: this.release }, request)));
        this.setBreadcrumbs(undefined);
    }
    getPossibleEventMessages(event) {
        if (event.message) {
            return [event.message];
        }
        if (event.exception) {
            try {
                const { type = '', value = '' } = (event.exception.values && event.exception.values[0]) || {};
                return [`${value}`, `${type}: ${value}`];
            }
            catch (e) {
                return [];
            }
        }
        return [];
    }
    isDeniedUrl(event) {
        if (!this.blacklistUrls.length) {
            return false;
        }
        const url = this.getEventFilterUrl(event);
        return !url
            ? false
            : this.blacklistUrls.some((pattern) => isMatchingPattern(url, pattern));
    }
    getEventFilterUrl(event) {
        try {
            if (event.exception) {
                const frames = event.exception.values &&
                    event.exception.values[0].stacktrace &&
                    event.exception.values[0].stacktrace.frames;
                return (frames && frames[frames.length - 1].filename) || null;
            }
            return null;
        }
        catch (e) {
            return null;
        }
    }
    extendState(newState) {
        this._state = Object.keys(newState).reduce((acc, key) => {
            const stateValue = this.state[key];
            const stateArray = Array.isArray(stateValue) ? stateValue : null;
            const newStateValue = newState[key];
            const newStateArray = Array.isArray(newStateValue)
                ? newStateValue
                : null;
            return __assign(acc, {
                [key]: stateArray || newStateArray
                    ? [...(stateArray || []), ...(newStateArray || [])]
                    : Object.assign(Object.assign({}, (typeof stateValue !== 'string' ? stateValue : {})), (typeof newStateValue !== 'string' ? newStateValue : {})),
            });
        }, {});
    }
    setKeyState(key, value) {
        this._state[key] = value;
    }
}

function fill(source, name, replacement) {
    const original = source[name];
    const wrapped = replacement(original);
    if (typeof wrapped === 'function') {
        try {
            wrapped.prototype = wrapped.prototype || {};
            Object.defineProperties(wrapped, {
                __original__: {
                    enumerable: false,
                    value: original,
                },
            });
        }
        catch (e) {
            //
        }
    }
    source[name] = wrapped;
}

function parseUrl(url) {
    if (!url) {
        return {};
    }
    const match = url.match(/^(([^:/?#]+):)?(\/\/([^/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?$/);
    if (!match) {
        return {};
    }
    // coerce to undefined values to empty string so we don't get 'undefined'
    const query = match[6] || '';
    const fragment = match[8] || '';
    return {
        host: match[4],
        path: match[5],
        protocol: match[2],
        relative: match[5] + query + fragment,
    };
}

function getFetchMethod(fetchArgs = []) {
    if ('Request' in window &&
        fetchArgs[0] instanceof Request &&
        fetchArgs[0].method) {
        return String(fetchArgs[0].method).toUpperCase();
    }
    if (fetchArgs[1] && fetchArgs[1].method) {
        return String(fetchArgs[1].method).toUpperCase();
    }
    return 'GET';
}
function getFetchUrl(fetchArgs = []) {
    if (typeof fetchArgs[0] === 'string') {
        return fetchArgs[0];
    }
    if ('Request' in window && fetchArgs[0] instanceof Request) {
        return fetchArgs[0].url;
    }
    return String(fetchArgs[0]);
}
function supportsFetch() {
    if (!('fetch' in window)) {
        return false;
    }
    try {
        // tslint:disable-next-line:no-unused-expression
        new Headers();
        // tslint:disable-next-line:no-unused-expression
        new Request('');
        // tslint:disable-next-line:no-unused-expression
        new Response();
        return true;
    }
    catch (e) {
        return false;
    }
}
// eslint-disable-next-line @typescript-eslint/ban-types
function isNativeFetch(func) {
    return (func &&
        /^function fetch\(\)\s+\{\s+\[native code\]\s+\}$/.test(func.toString()));
}
function supportsNativeFetch() {
    if (!supportsFetch()) {
        return false;
    }
    // Fast path to avoid DOM I/O
    // tslint:disable-next-line:no-unbound-method
    if (isNativeFetch(window.fetch)) {
        return true;
    }
    // window.fetch is implemented, but is polyfilled or already wrapped (e.g: by a chrome extension)
    // so create a "pure" iframe to see if that has native fetch
    let result = false;
    const doc = window.document;
    // tslint:disable-next-line:no-unbound-method deprecation
    if (doc && typeof doc.createElement === `function`) {
        try {
            const sandbox = doc.createElement('iframe');
            sandbox.sandbox.add('allow-scripts');
            sandbox.sandbox.add('allow-same-origin');
            sandbox.hidden = true;
            doc.head.appendChild(sandbox);
            if (sandbox.contentWindow && sandbox.contentWindow.fetch) {
                // tslint:disable-next-line:no-unbound-method
                result = isNativeFetch(sandbox.contentWindow.fetch);
            }
            doc.head.removeChild(sandbox);
        }
        catch (err) {
            // 'Could not create sandbox iframe for pure fetch check, bailing to window.fetch'
        }
    }
    return result;
}

function getElementSelector(el) {
    const elem = el;
    const out = [];
    if (!elem || !elem.tagName) {
        return '';
    }
    out.push(elem.tagName.toLowerCase());
    if (elem.id) {
        out.push(`#${elem.id}`);
    }
    const { className } = elem;
    if (className && typeof className === 'string') {
        const classes = className.split(/\s+/);
        out.concat(classes.map((klass) => `.${klass}`));
    }
    ['type', 'name', 'title', 'alt', 'automation-id'].forEach((key) => {
        const attr = elem.getAttribute(key);
        if (attr) {
            out.push(`[${key}="${attr}"]`);
        }
    });
    return out.join('');
}

function htmlTreeAsString(elem) {
    // try/catch both:
    // - accessing event.target (see getsentry/raven-js#838, #768)
    // - `htmlTreeAsString` because it's complex, and just accessing the DOM incorrectly
    // - can throw an exception in some circumstances.
    try {
        let currentElem = elem;
        const MAX_TRAVERSE_HEIGHT = 5;
        const MAX_OUTPUT_LEN = 80;
        const out = [];
        let height = 0;
        let len = 0;
        const separator = ' > ';
        const sepLength = separator.length;
        let nextStr;
        while (currentElem && height++ < MAX_TRAVERSE_HEIGHT) {
            nextStr = getElementSelector(currentElem);
            // bail out if
            // - nextStr is the 'html' element
            // - the length of the string that would be created exceeds MAX_OUTPUT_LEN
            //   (ignore this limit if we are on the first iteration)
            if (nextStr === 'html' ||
                (height > 1 &&
                    len + out.length * sepLength + nextStr.length >= MAX_OUTPUT_LEN)) {
                break;
            }
            out.push(nextStr);
            len += nextStr.length;
            currentElem = currentElem.parentNode;
        }
        return out.reverse().join(separator);
    }
    catch (_oO) {
        return '<unknown>';
    }
}

function safeJoin(input, delimiter = ' ') {
    if (!Array.isArray(input)) {
        return '';
    }
    return input
        .map((value) => {
        try {
            return String(value);
        }
        catch (e) {
            return '';
        }
    })
        .filter((v) => v !== '')
        .join(delimiter);
}

class BreadcrumbPlugin {
    constructor(client) {
        this.client = client;
        this.subscriptions = [];
        this.initDOM();
        this.initConsole();
        this.initFetch();
        this.initHistory();
        this.initXHR();
    }
    initXHR() {
        if (!('XMLHttpRequest' in window)) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const xhrproto = XMLHttpRequest.prototype;
        fill(xhrproto, 'open', (originalOpen) => {
            return function (...args) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const xhr = this; // tslint:disable-line:no-this-assignment
                const url = args[1];
                xhr.__sentry_xhr__ = {
                    method: typeof args[0] === 'string' ? args[0].toUpperCase() : args[0],
                    url: args[1],
                };
                // if Sentry key appears in URL, don't capture it as a request
                if (typeof url === 'string' &&
                    xhr.__sentry_xhr__.method === 'POST' &&
                    url.match(/sentry_key/)) {
                    xhr.__sentry_own_request__ = true;
                }
                const onreadystatechangeHandler = () => {
                    if (xhr.readyState === 4) {
                        try {
                            // touching statusCode in some platforms throws
                            // an exception
                            if (xhr.__sentry_xhr__) {
                                xhr.__sentry_xhr__.status_code = xhr.status;
                            }
                        }
                        catch (e) {
                            /* do nothing */
                        }
                        self.xhrBreadcrumb({
                            args,
                            endTimestamp: Date.now(),
                            startTimestamp: Date.now(),
                            xhr,
                        });
                    }
                };
                if ('onreadystatechange' in xhr &&
                    typeof xhr.onreadystatechange === 'function') {
                    fill(xhr, 'onreadystatechange', (original) => {
                        return function onreadystatechange(...readyStateArgs) {
                            onreadystatechangeHandler();
                            original.apply(xhr, readyStateArgs);
                        };
                    });
                }
                else {
                    xhr.addEventListener('readystatechange', onreadystatechangeHandler);
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return originalOpen.apply(xhr, args);
            };
        });
        fill(xhrproto, 'send', (originalSend) => {
            return function send(...args) {
                self.xhrBreadcrumb({
                    args,
                    startTimestamp: Date.now(),
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    xhr: this,
                });
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return originalSend.apply(this, args);
            };
        });
    }
    initHistory() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const oldOnPopState = window.onpopstate;
        window.onpopstate = function (...args) {
            const to = window.location.href;
            // keep track of the current URL state, as we always receive only the updated state
            const from = self.lastHref;
            self.lastHref = to;
            self.historyBreadcrumb({
                from,
                to,
            });
            if (oldOnPopState) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return oldOnPopState.apply(this, args);
            }
        };
        /** @hidden */
        function historyReplacementFunction(originalHistoryFunction) {
            return function (...args) {
                const url = args.length > 2 ? args[2] : undefined;
                if (url) {
                    // coerce to string (this is what pushState does)
                    const from = self.lastHref;
                    const to = String(url);
                    // keep track of the current URL state, as we always receive only the updated state
                    self.lastHref = to;
                    self.historyBreadcrumb({
                        from,
                        to,
                    });
                }
                return originalHistoryFunction.apply(this, args);
            };
        }
        fill(window.history, 'pushState', historyReplacementFunction);
        fill(window.history, 'replaceState', historyReplacementFunction);
    }
    destroy() {
        this.subscriptions.forEach((fn) => fn());
    }
    initDOM() {
        const clickEventHandler = (event) => this.prepareDomEvent('click', event);
        const keypressEventHandler = (event) => this.prepareDomEvent('keypress', event);
        window.document.addEventListener('click', clickEventHandler, false);
        window.document.addEventListener('keypress', keypressEventHandler, false);
        this.subscriptions.push(() => {
            window.document.removeEventListener('click', clickEventHandler, false);
            window.document.removeEventListener('keypress', keypressEventHandler, false);
        });
    }
    prepareDomEvent(name, event) {
        let target;
        // Accessing event.target can throw (see getsentry/raven-js#838, #768)
        try {
            target = event.target
                ? htmlTreeAsString(event.target)
                : htmlTreeAsString(event);
        }
        catch (e) {
            target = '<unknown>';
        }
        if (target.length === 0) {
            return;
        }
        this.client.addBreadcrumb({
            category: `ui.${name}`,
            message: target,
        });
    }
    initFetch() {
        if (!supportsNativeFetch()) {
            return;
        }
        fill(window, 'fetch', (originalFetch) => {
            return (...args) => {
                const commonHandlerData = {
                    args,
                    fetchData: {
                        method: getFetchMethod(args),
                        url: getFetchUrl(args),
                    },
                    startTimestamp: Date.now(),
                };
                this.fetchBreadcrumb(Object.assign({}, commonHandlerData));
                return originalFetch.apply(window, args).then((response) => {
                    this.fetchBreadcrumb(Object.assign(Object.assign({}, commonHandlerData), { endTimestamp: Date.now(), response }));
                    return response;
                }, (error) => {
                    this.fetchBreadcrumb(Object.assign(Object.assign({}, commonHandlerData), { endTimestamp: Date.now(), error }));
                    throw error;
                });
            };
        });
    }
    fetchBreadcrumb(handlerData) {
        // We only capture complete fetch requests
        if (!handlerData.endTimestamp) {
            return;
        }
        if (handlerData.fetchData.url.match(/sentry_key/) &&
            handlerData.fetchData.method === 'POST') {
            // We will not create breadcrumbs for fetch requests that contain `sentry_key` (internal sentry requests)
            return;
        }
        if (handlerData.error) {
            this.client.addBreadcrumb({
                category: 'fetch',
                data: handlerData.fetchData,
                level: "error" /* error */,
                type: 'http',
            });
        }
        else {
            this.client.addBreadcrumb({
                category: 'fetch',
                data: Object.assign(Object.assign({}, handlerData.fetchData), { status_code: handlerData.response.status }),
                type: 'http',
            });
        }
    }
    xhrBreadcrumb(handlerData) {
        if (handlerData.endTimestamp) {
            // We only capture complete, non-sentry requests
            if (handlerData.xhr.__sentry_own_request__) {
                return;
            }
            this.client.addBreadcrumb({
                category: 'xhr',
                data: handlerData.xhr.__sentry_xhr__,
                type: 'http',
            });
            return;
        }
    }
    initConsole() {
        [
            'debug',
            'info',
            'warn',
            'error',
            'log',
            'assert',
        ].forEach((level) => {
            if (!(level in window.console)) {
                return;
            }
            fill(window.console, level, (originalConsoleLevel) => {
                return (...args) => {
                    // {args, level};
                    const breadcrumb = {
                        category: 'console',
                        data: {
                            arguments: args.map((arg) => {
                                // если аргументы сериализуются с ошибкой
                                // приводим значение к строке и возвращаем его
                                try {
                                    JSON.stringify(arg);
                                    return arg;
                                }
                                catch (e) {
                                    return Object.prototype.toString.call(arg);
                                }
                            }),
                            logger: 'console',
                        },
                        level: level,
                        message: safeJoin(args, ' '),
                    };
                    if (level === 'assert') {
                        if (args[0] === false) {
                            breadcrumb.message = `Assertion failed: ${safeJoin(args.slice(1), ' ') || 'console.assert'}`;
                            breadcrumb.data.arguments = args.slice(1);
                        }
                        else {
                            // Don't capture a breadcrumb for passed assertions
                            return;
                        }
                    }
                    this.client.addBreadcrumb(breadcrumb);
                    // this fails for some browsers. :(
                    if (originalConsoleLevel) {
                        Function.prototype.apply.call(originalConsoleLevel, window.console, args);
                    }
                };
            });
        });
    }
    historyBreadcrumb(handlerData) {
        let from = handlerData.from;
        let to = handlerData.to;
        const parsedLoc = parseUrl(window.location.href);
        let parsedFrom = parseUrl(from);
        const parsedTo = parseUrl(to);
        // Initial pushState doesn't provide `from` information
        if (!parsedFrom.path) {
            parsedFrom = parsedLoc;
        }
        // Use only the path component of the URL if the URL matches the current
        // document (almost all the time when using pushState)
        if (parsedLoc.protocol === parsedTo.protocol &&
            parsedLoc.host === parsedTo.host) {
            // tslint:disable-next-line:no-parameter-reassignment
            to = parsedTo.relative;
        }
        if (parsedLoc.protocol === parsedFrom.protocol &&
            parsedLoc.host === parsedFrom.host) {
            // tslint:disable-next-line:no-parameter-reassignment
            from = parsedFrom.relative;
        }
        this.client.addBreadcrumb({
            category: 'navigation',
            data: {
                from,
                to,
            },
        });
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { BreadcrumbPlugin, BrowserMicroSentryClient };
//# sourceMappingURL=micro-sentry-browser.js.map
